import { type ReactNode, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog with WCAG 2.1 AA basics:
 *   - role="dialog" + aria-modal + aria-labelledby pointing at the title.
 *   - Focus is captured into the dialog on open and restored to the
 *     previously-focused element on close.
 *   - Tab/Shift+Tab cycle within the dialog (focus trap).
 *   - Escape closes the dialog.
 *   - Body scroll is locked while open so screen-reader users can't drift
 *     into the inert background.
 *   - Backdrop is `aria-hidden` and does NOT close on click — a stray click
 *     outside must not discard in-progress form work (QA #9 / WMS-10). The
 *     dialog closes only via the × button, a Cancel action, confirming the
 *     operation, or the Escape key (a deliberate keypress, not an accident).
 */
const SIZE_CLASS: Record<'md' | 'lg' | 'xl' | 'wide', string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  wide: 'max-w-7xl',
};

export function Dialog({
  title,
  onClose,
  children,
  size = 'md',
  height = 'auto',
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl' | 'wide';
  /** Full-height workspace for long catalogue pickers (WMS-47/48). */
  height?: 'auto' | 'tall';
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`dialog-title-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    const previousActive = document.activeElement as HTMLElement | null;

    queueMicrotask(() => {
      const root = containerRef.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? root).focus();
    });

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = containerRef.current;
      if (!root) return;
      const focusable = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
      );
      if (focusable.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previousActive?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      aria-hidden="true"
    >
      <div
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // Cap the height and scroll internally so a tall form (many fields)
        // never pushes its Save/Cancel buttons off-screen — body scroll is
        // locked while open, so without this the buttons are unreachable
        // (WMS-15).
        className={`w-full ${SIZE_CLASS[size]} ${height === 'tall' ? 'h-[90dvh]' : ''} max-h-[90dvh] overflow-y-auto rounded-lg border border-border bg-background p-5 shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-primary`}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 id={titleId} className="text-base font-semibold">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
